import {
  Box,
  Button,
  CircularProgress,
  Typography,
  List,
  ListItem,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Chip,
  Collapse,
  Stack,
  Pagination,
} from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import DocumentUploader from './DocumentUploader'
import { getFileIcon } from '@components/Helpers'
import { FC, useState } from 'react'
import ConfirmDialog from '@components/dialogs/ConfirmDialog'
import { DocumentService } from '@services/StorageService'
import { DocumentDTO } from '../../services/dtos'
import { Download, Eye, Trash2 } from 'lucide-react'
import { EntityNameEnum } from '@utils/enums'

type Props = {
  entityName: EntityNameEnum
  entityId?: number
  documents: DocumentDTO[]
  isEditingDocuments: boolean
  isLoading?: boolean
  setisEditingDocuments: (value: boolean) => void
  handleOnFileUploaded: (document: DocumentDTO) => void
  setSelectedDocument: (document: DocumentDTO) => void
  onDocumentDeleted: (document: DocumentDTO) => void
  listClassName?: string
  page?: number
  totalCount?: number
  pageSize?: number
  onPageChange?: (page: number) => void
}

export const DocumentsList: FC<Props> = ({
  documents,
  isEditingDocuments,
  isLoading,
  setisEditingDocuments,
  handleOnFileUploaded,
  setSelectedDocument,
  entityName,
  entityId,
  onDocumentDeleted,
  listClassName,
  page,
  totalCount,
  pageSize,
  onPageChange,
}) => {
  const [deletingDocument, setDeletingDocument] = useState<DocumentDTO | null>(
    null,
  )
  const [uploadedFile, setUploadedFile] = useState<{
    url: string
    mimetype: string
  } | null>(null)

  const onConfirmDelete = () => {
    if (!deletingDocument) {
      return
    }
    DocumentService.deleteDocument(deletingDocument.id)
    onDocumentDeleted(deletingDocument)
    setDeletingDocument(null)
  }

  const onDocumentUploaded = (document: DocumentDTO) => {
    handleOnFileUploaded(document)
    setisEditingDocuments(false)
    setUploadedFile(null)
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={0}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Button
                startIcon={
                  <AddIcon
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      transform: `rotate(${
                        isEditingDocuments ? '225deg' : '0deg'
                      })`,
                      transition: 'transform 300ms ease-in-out',
                    }}
                  />
                }
                onClick={() => {
                  const isEditing = !isEditingDocuments
                  if (!isEditing && uploadedFile) {
                    setUploadedFile(null)
                  }
                  setisEditingDocuments(!isEditingDocuments)
                }}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 12px' },
                }}
              >
                <span className="hidden sm:inline">
                  {isEditingDocuments ? 'Fermer' : 'Ajouter un document'}
                </span>
                <span className="inline sm:hidden">
                  {isEditingDocuments ? 'Fermer' : 'Ajouter'}
                </span>
              </Button>
            </Box>

            <Collapse in={isEditingDocuments}>
              <Box mb={0}>
                <DocumentUploader
                  uploadedFile={uploadedFile}
                  setUploadedFile={setUploadedFile}
                  onUploaded={onDocumentUploaded}
                  entityId={entityId}
                  entityName={entityName}
                />
              </Box>
            </Collapse>

            <List sx={{ width: '100%' }} className={listClassName}>
              {isLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : null}
              {!isLoading &&
                documents.map((doc) => (
                  <ListItem
                    key={doc.id}
                    sx={{
                      mb: 1,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                    secondaryAction={
                      <Stack
                        direction="row"
                        spacing={{ xs: 0, sm: 0.1 }}
                        mt={3}
                      >
                        <Tooltip title="View Document">
                          <IconButton
                            edge="end"
                            onClick={() => setSelectedDocument(doc)}
                            size="small"
                            color="primary"
                          >
                            <Eye size={20} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            edge="end"
                            onClick={() =>
                              window.open(
                                `${process.env.REACT_APP_API_URL}${
                                  doc.url.startsWith('/') ? '' : '/'
                                }${doc.url}`,
                                '_blank',
                              )
                            }
                            size="small"
                          >
                            <Download size={20} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            edge="end"
                            onClick={() => setDeletingDocument(doc)}
                            size="small"
                            color="error"
                          >
                            <Trash2 size={20} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <Stack
                      direction="row"
                      spacing={{ xs: 0.5, sm: 1 }}
                      alignItems="center"
                    >
                      <Box sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                        {getFileIcon(doc.gedFile.mimeType)}
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {doc.gedFile.filename}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            sx={{
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              height: { xs: '18px', sm: '24px' },
                            }}
                            label={doc.fileType}
                            size="small"
                            variant="outlined"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          >
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </ListItem>
                ))}
              {!isLoading && documents.length === 0 && (
                <Box
                  textAlign="center"
                  py={2}
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Typography color="text.secondary" fontSize={12}>
                    Aucun document trouvé
                  </Typography>
                </Box>
              )}
            </List>
            {onPageChange &&
              totalCount != null &&
              pageSize != null &&
              totalCount > pageSize && (
                <Box display="flex" justifyContent="center" py={1}>
                  <Pagination
                    count={Math.ceil(totalCount / pageSize)}
                    page={page ?? 1}
                    onChange={(_e, value) => onPageChange(value)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
          </Stack>
        </CardContent>
      </Card>
      <ConfirmDialog
        severity="error"
        open={!!deletingDocument}
        onCancel={() => setDeletingDocument(null)}
        onConfirm={onConfirmDelete}
        title="Supprimer le document?"
        message="Voulez-vous vraiment supprimer ce document?"
      />
    </>
  )
}

export default DocumentsList
